// Libraries
import { Box, List, ListItem, Text } from '@chakra-ui/react';
import { ReactElement } from 'react';
// Utils
import { colors } from '@theme/foundations';
// Types
import { ChartLineStyles } from '@globalTypes/index';
import { ChartLegendProps, CustomLegendStyle, CustomLineStyles } from './ChartLegend.type';

/**
 * Object with the styles required to render the line
 */
const lineStyles: CustomLineStyles = {
  [ChartLineStyles.black]: {
    borderStyle: 'solid',
    borderColor: colors.blackBase,
  },
  [ChartLineStyles.blackDashed]: {
    borderStyle: 'dashed',
    borderColor: colors.blackBase,
  },
  [ChartLineStyles.light]: {
    borderStyle: 'solid',
    borderColor: colors.base,
  },
  [ChartLineStyles.lightDashed]: {
    borderStyle: 'dashed',
    borderColor: colors.base,
  },
  [CustomLegendStyle.blackBase]: {
    borderStyle: 'solid',
    borderColor: colors.grey[100],
  },
  [CustomLegendStyle.blackBaseDashed]: {
    borderStyle: 'dashed',
    borderColor: colors.grey[100],
  },
  [CustomLegendStyle.scatter]: {
    w: 2,
    h: 2,
    borderStyle: 'solid',
    borderColor: colors.grey[100],
    borderRadius: 10,
    borderWidth: 2,
  },
  [CustomLegendStyle.scatterYellow]: {
    w: 2,
    h: 2,
    borderStyle: 'solid',
    background: colors.orange,
    borderColor: colors.orange,
    borderRadius: 10,
    borderWidth: 2,
  },
  [CustomLegendStyle.scatterBlue]: {
    w: 2,
    h: 2,
    borderStyle: 'solid',
    background: colors.base,
    borderColor: colors.base,
    borderRadius: 10,
    borderWidth: 2,
  },
  [CustomLegendStyle.blackDotted]: {
    borderLeftStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderTopStyle: 'dotted',
    borderColor: colors.blackBase,
    borderWidth: 2,
    w: 4,
  },
};

/**
 * Represents a chart legend component that displays items with titles and corresponding line styles.
 *
 * @component
 * @param {ChartLegendProps} props - The props for the ChartLegend component.
 * @param {LegendItem[]} props.items - An array of chart legend items, each specifying a title and style variation.
 * @returns {ReactElement} The rendered ChartLegend component.
 */
export const ChartLegend = ({ items }: ChartLegendProps): ReactElement => {
  return (
    <List display='flex' gap={3} opacity={0.7} flexWrap='wrap' justifyContent='center'>
      {items.map((item) => (
        <ListItem key={item.title} display='flex' alignItems='center'>
          <Text fontSize='s' mr={3} color='grey.100'>
            {item.title}
          </Text>
          <Box w={6} borderWidth='thin' {...lineStyles[item.styleKey]} />
        </ListItem>
      ))}
    </List>
  );
};
